import React, { useMemo } from 'react';
import { Box, Button, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { KSTABLES } from '../_shared/wallet-app/chains';
import { BRAND } from '../theme';
import { formatUnits, parseUnits, cmpWei } from '../lib/wei';

// KStableAmountInput
//
// Pill row of supported kStables + a numeric input + a live "Max" button.
// Refuses to accept characters that aren't digits or a single decimal point.
// Reports validity / over-balance via callbacks so the parent can disable
// the Bridge button.
//
// Props:
//   symbol            'kUSD'…
//   onSymbolChange    (sym) => void
//   amount            decimal string (e.g. "50.5")
//   onAmountChange    (str) => void
//   decimals          token decimals (default 6)
//   balanceWei        BigInt-string source-chain balance (for Max + over-balance)
//   supportedSymbols  Subset of kStables that the selected chain pair supports
//   error             Optional error string to show below the input

export default function KStableAmountInput({
  symbol,
  onSymbolChange,
  amount,
  onAmountChange,
  decimals = 6,
  balanceWei = null,
  supportedSymbols = null,
  error = null,
}) {
  const allowed = useMemo(() => {
    if (!supportedSymbols || supportedSymbols.length === 0) return KSTABLES;
    const set = new Set(supportedSymbols);
    return KSTABLES.filter((k) => set.has(k.symbol));
  }, [supportedSymbols]);

  const handleAmountChange = (e) => {
    let v = e.target.value;
    // Strip anything except digits + at most one dot.
    v = v.replace(/[^\d.]/g, '');
    // Collapse multiple dots into one.
    const firstDot = v.indexOf('.');
    if (firstDot >= 0) {
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
    }
    // Reject too-many-decimal places (defensive — parseUnits would throw).
    const dot = v.indexOf('.');
    if (dot >= 0 && v.length - dot - 1 > decimals) {
      v = v.slice(0, dot + 1 + decimals);
    }
    onAmountChange(v);
  };

  const onMax = () => {
    if (!balanceWei) return;
    onAmountChange(formatUnits(balanceWei, decimals));
  };

  const overBalance = useMemo(() => {
    if (!balanceWei || !amount) return false;
    try {
      const wei = parseUnits(amount, decimals);
      return cmpWei(wei, balanceWei) > 0;
    } catch {
      return false;
    }
  }, [amount, balanceWei, decimals]);

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        backgroundColor: 'rgba(255,255,255,0.03)',
        border: `1px solid ${overBalance || error ? BRAND.error : BRAND.border}`,
      }}
      data-testid="amount-input-card"
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography variant="caption" sx={{ color: BRAND.textSecondary, fontWeight: 600 }}>
          AMOUNT
        </Typography>
        {balanceWei != null && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" sx={{ color: BRAND.textSecondary }}>
              Balance: {formatUnits(balanceWei, decimals)} {symbol}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={onMax}
              sx={{
                minWidth: 0,
                px: 1,
                py: 0.25,
                fontSize: '0.7rem',
                borderColor: BRAND.violet,
                color: BRAND.violetLight,
                '&:hover': { borderColor: BRAND.violetLight, backgroundColor: 'rgba(124,58,237,0.08)' },
              }}
              data-testid="max-button"
            >
              MAX
            </Button>
          </Stack>
        )}
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          fullWidth
          variant="standard"
          value={amount}
          onChange={handleAmountChange}
          placeholder="0.00"
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: { xs: '1.7rem', sm: '2rem' },
              fontWeight: 700,
              color: BRAND.textPrimary,
              '& input': { p: 0 },
            },
          }}
          inputProps={{
            inputMode: 'decimal',
            'data-testid': 'amount-input',
          }}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={symbol}
          onChange={(_, v) => v && onSymbolChange(v)}
          sx={{
            // wrap so the full bridgeable set (kUSD + commodities + future FX)
            // shows across rows instead of clipping at 4.
            flexWrap: 'wrap',
            gap: 0.5,
            '& .MuiToggleButton-root': {
              borderRadius: '8px !important',
              border: `1px solid ${BRAND.border}`,
              color: BRAND.textSecondary,
              fontWeight: 700,
              fontSize: '0.8rem',
              px: 1.25,
              py: 0.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(124,58,237,0.18)',
                color: BRAND.violetLight,
                borderColor: BRAND.violet,
              },
            },
          }}
        >
          {allowed.map((k) => (
            <ToggleButton
              key={k.symbol}
              value={k.symbol}
              data-testid={`kstable-pill-${k.symbol}`}
            >
              {k.symbol}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {overBalance && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 1, color: BRAND.error }}
          data-testid="over-balance-error"
        >
          Amount exceeds your {symbol} balance on the source chain.
        </Typography>
      )}
      {error && !overBalance && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: BRAND.error }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
